/**
 * Long-lived session socket to `/api/chat/ws/chat-session` (protocol: backend utils/chat_session.py).
 *
 * One per tab. Runs live on the server independently of this socket: it streams the
 * focused conversation, reports every running conversation, and carries start / steer /
 * cancel. It reconnects with backoff and resumes the focused run from the last seen seq.
 */

import { ChatObjectType } from "@/app/chatState";
import { ChatRunEvent } from "./websocket.gen";

export type RunStatus = "running" | "completed" | "cancelled" | "failed";

export type RunSummary = {
    run_id: number;
    conversation_id: number;
    org_id: number;
    tag_ids: number[];
    status: RunStatus;
    started_at: string;
    ended_at: string | null;
    error: string | null;
};

export type RunEventRecord = { seq: number; event: ChatRunEvent };

export type SessionMessage =
    | { type: "runs"; runs: RunSummary[] }
    | ({ type: "run_status" } & RunSummary)
    | { type: "subscribed"; conversation_id: number | null; tag_ids: number[]; request_id?: string }
    | { type: "run_snapshot"; conversation_id: number; run: RunSummary | null; events: RunEventRecord[]; last_seq: number; resumed: boolean }
    | { type: "run_event"; run_id: number; conversation_id: number; seq: number; event: ChatRunEvent }
    | { type: "started" | "steered"; run_id: number; conversation_id: number; tag_ids: number[]; request_id?: string }
    | { type: "steer_queued"; run_id: number; conversation_id: number; request_id?: string }
    | { type: "cancel_requested"; conversation_id: number; run_id: number | null; cancelled: boolean; request_id?: string }
    | { type: "command_error"; command: string | null; detail: string; request_id?: string }
    | { type: "pong"; request_id?: string };

export type ObjectKey = { type: ChatObjectType; id: number };

export type StartPayload = {
    tag_ids: number[];
    prompt: string;
    is_injection?: boolean;
    selected_objects?: ObjectKey[];
    open_object?: ObjectKey;
    artifact_update?: { tag_ids: number[] | null; variable_name: string | null; content_diff?: string | null };
    replay_message_id?: number;
    skill_key?: string;
};

type StartReply = Extract<SessionMessage, { type: "started" | "steered" }>;
type SteerReply = Extract<SessionMessage, { type: "steer_queued" }> | StartReply;
type CancelReply = Extract<SessionMessage, { type: "cancel_requested" }>;

type Listener = (message: SessionMessage) => void;

const REQUEST_TIMEOUT_MS = 15_000;
const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 10_000;

export class ChatSession {
    private ws: WebSocket | null = null;
    private wanted = false;
    private retry = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private outbox: string[] = [];
    private listeners = new Set<Listener>();
    private pending = new Map<string, { resolve: (m: SessionMessage) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
    private nextRequestId = 1;
    private focusTagIds: number[] | null = null;
    private focusRun: { run_id: number; conversation_id: number } | null = null;
    private lastSeq = new Map<number, number>();
    private onHandshakeFailed: (() => void) | null = null;

    /**
     * Called when a socket closes without ever opening. A failed handshake looks the same to
     * the browser whether the session was revoked or the network blipped, so the app checks.
     */
    setHandshakeFailedHandler(handler: (() => void) | null) {
        this.onHandshakeFailed = handler;
    }

    connect() {
        this.wanted = true;
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
        this.open();
    }

    /** Drop the socket and connect again, e.g. after the active organisation changed. */
    reconnect() {
        this.ws?.close();
        this.ws = null;
        this.connect();
    }

    close() {
        this.wanted = false;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.ws?.close();
        this.ws = null;
        this.focusTagIds = null;
        this.focusRun = null;
        this.lastSeq.clear();
        this.outbox = [];
    }

    addListener(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /** Focus a tag scope: its conversation streams here; everything else only reports status. */
    subscribe(tagIds: number[]) {
        this.focusTagIds = [...tagIds].sort((a, b) => a - b);
        this.focusRun = null;
        this.lastSeq.clear();
        this.send({ type: "subscribe", tag_ids: this.focusTagIds });
    }

    start(payload: StartPayload) {
        return this.request<StartReply>({ type: "start", ...payload });
    }

    steer(conversationId: number, prompt: string, selection?: { selected_objects?: ObjectKey[]; open_object?: ObjectKey }) {
        return this.request<SteerReply>({ type: "steer", conversation_id: conversationId, prompt, ...selection });
    }

    cancel(conversationId: number) {
        return this.request<CancelReply>({ type: "cancel", conversation_id: conversationId });
    }

    private open() {
        const url = window.location.origin.replace(/^http/, "ws") + "/api/chat/ws/chat-session";
        const ws = new WebSocket(url);
        this.ws = ws;
        let opened = false;

        ws.onopen = () => {
            if (this.ws !== ws) return;
            opened = true;
            this.retry = 0;
            if (this.focusTagIds) {
                // A reconnect: pick the focused run up where we left it.
                const resume = this.focusRun ? { run_id: this.focusRun.run_id, after_seq: this.lastSeq.get(this.focusRun.run_id) ?? 0 } : undefined;
                ws.send(JSON.stringify({ type: "subscribe", tag_ids: this.focusTagIds, resume }));
            }
            const queued = this.outbox;
            this.outbox = [];
            queued.forEach((data) => ws.send(data));
            if (this.pingTimer) clearInterval(this.pingTimer);
            this.pingTimer = setInterval(() => this.send({ type: "ping" }), PING_INTERVAL_MS);
        };

        ws.onmessage = (event) => {
            if (this.ws !== ws) return;
            let message: SessionMessage;
            try {
                message = JSON.parse(event.data);
            } catch {
                return;
            }
            this.receive(message);
        };

        ws.onclose = () => {
            if (this.ws !== ws) return;
            this.ws = null;
            if (this.pingTimer) clearInterval(this.pingTimer);
            this.pingTimer = null;
            if (!this.wanted) return;
            if (!opened) this.onHandshakeFailed?.();
            const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.retry++);
            this.reconnectTimer = setTimeout(() => {
                this.reconnectTimer = null;
                if (this.wanted) this.open();
            }, delay);
        };

        ws.onerror = (event) => console.error("chat session socket error", event);
    }

    private send(command: object) {
        const data = JSON.stringify(command);
        if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(data);
        else if ((command as { type?: string }).type !== "ping" && (command as { type?: string }).type !== "subscribe") this.outbox.push(data);
        // A subscribe made while offline is replayed from focusTagIds on open.
        if (!this.ws && this.wanted && !this.reconnectTimer) this.open();
    }

    private request<T extends SessionMessage>(command: object): Promise<T> {
        const request_id = String(this.nextRequestId++);
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => {
                this.pending.delete(request_id);
                reject(new Error("chat session request timed out"));
            }, REQUEST_TIMEOUT_MS);
            this.pending.set(request_id, { resolve: resolve as (m: SessionMessage) => void, reject, timer });
            this.send({ ...command, request_id });
        });
    }

    private receive(message: SessionMessage) {
        if ("request_id" in message && message.request_id && this.pending.has(message.request_id)) {
            const pending = this.pending.get(message.request_id)!;
            this.pending.delete(message.request_id);
            clearTimeout(pending.timer);
            if (message.type === "command_error") pending.reject(new Error(message.detail));
            else pending.resolve(message);
        }

        switch (message.type) {
            case "run_event": {
                const last = this.lastSeq.get(message.run_id) ?? 0;
                if (message.seq <= last) return;
                this.lastSeq.set(message.run_id, message.seq);
                this.focusRun = { run_id: message.run_id, conversation_id: message.conversation_id };
                break;
            }
            case "run_snapshot": {
                const run = message.run;
                if (run) {
                    const last = this.lastSeq.get(run.run_id) ?? 0;
                    if (message.resumed) message = { ...message, events: message.events.filter((e) => e.seq > last) };
                    this.lastSeq.set(run.run_id, Math.max(last, message.last_seq));
                    this.focusRun = { run_id: run.run_id, conversation_id: message.conversation_id };
                }
                break;
            }
            case "run_status":
                if (message.status !== "running") {
                    this.lastSeq.delete(message.run_id);
                    if (this.focusRun?.run_id === message.run_id) this.focusRun = null;
                }
                break;
        }

        this.listeners.forEach((listener) => {
            try {
                listener(message);
            } catch (e) {
                console.error("chat session listener failed", e);
            }
        });
    }
}

export const chatSession = new ChatSession();
